import { SkupremeModal } from '@components/Modal';
import { HoverBorderButton, StyledButton } from '@elements/Button';
import { BoldText, MuteText } from '@elements/Common';
import { WarehouseLocationLabelHook } from '@hooks/label';
import { useEffect, useState } from 'react';
import { Radio } from 'semantic-ui-react';
import {
  labelSizeOptions,
  type labelSizes,
  locationLabelStatusColors,
  pageSizeOptions,
  type pageSizes,
} from '../warehouse/common';
import { DateFormat, DateUtility } from '@utility/date';
import { ColoredPill } from '@elements/Pills';
import { Circle, DownloadSimple } from '@phosphor-icons/react';
import styled from 'styled-components';
import { CommonUtility } from '@utility/common';
import { useAuth } from '@contexts/auth';
import { LocationService, PalletService } from '@utility/services';
import { ToastMessage } from '@utility/toast';
import { theme } from '@utility/theme';
import { PreviousPalletLabelHook } from '@hooks/index';

const BorderedBox = styled.div`
  padding: 15px;
  border: 1px solid ${({ theme }) => theme.colors.semanticBorder};
  border-radius: 5px;
`;

const OptionsContainer = styled.div`
  .selected {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

type formValueType = {
  labelSize: (typeof labelSizes)[keyof typeof labelSizes];
  pageSize: (typeof pageSizes)[keyof typeof pageSizes];
};

type GeneratePalletLabelPopupProps = {
  warehouseId: string;
  open: boolean;
  onClose: () => void;
  palletNumber: string;
};

export function GeneratePalletLabelPopup({
  warehouseId,
  open,
  onClose,
  palletNumber,
}: GeneratePalletLabelPopupProps) {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setSubmitted(false);
    }
  }, [open]);

  const { data, loading: previousLabelLoading } = PreviousPalletLabelHook(
    warehouseId,
    palletNumber,
  );
  const { user } = useAuth();

  const [formValue, setFormValue] = useState<formValueType>({
    labelSize: null,
    pageSize: null,
  });

  const changeLabelSize = (size: (typeof labelSizes)[keyof typeof labelSizes]) => {
    setFormValue(prev => ({ ...prev, labelSize: size }));
  };

  const changePageSize = (size: (typeof pageSizes)[keyof typeof pageSizes]) => {
    setFormValue(prev => ({ ...prev, pageSize: size }));
  };

  const onSubmit = async () => {
    try {
      setLoading(true);
      const payload = {
        labelSize: formValue.labelSize,
        pageSize: formValue.pageSize,
        warehouseId,
        palletNumbers: [palletNumber],
      };
      await PalletService.generatePalletLabels(payload);
      setSubmitted(true);
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  const downloadPreviousExport = () => {
    if (data?.downloadUrl) {
      window.open(data.downloadUrl, '_blank');
    }
  };

  if (submitted) {
    return (
      <SkupremeModal
        size="mini"
        open={open}
        onClose={onClose}
        title={`We are preparing your label for ${palletNumber}`}
      >
        <span>
          We'll email you at <BoldText>{user.email}</BoldText> when ready.
        </span>
        <div className="d-flex align-items-center justify-content-end pt-4">
          <HoverBorderButton onClick={onClose}>Close</HoverBorderButton>
          <StyledButton onClick={onClose} className="ml-2 mr-0">
            Got It
          </StyledButton>
        </div>
      </SkupremeModal>
    );
  }

  return (
    <SkupremeModal
      size="tiny"
      open={open}
      onClose={onClose}
      title={`Generate Label for ${palletNumber}`}
    >
      <OptionsContainer className="d-flex align-items-center mb-3">
        <BoldText>Label Size</BoldText>
        {labelSizeOptions.map(option => (
          <div
            key={option.key}
            className="ml-4 d-flex align-items-center cursor-pointer"
            onClick={() => changeLabelSize(option.value)}
          >
            <Radio checked={formValue.labelSize === option.value} />
            <MuteText
              className={`ml-1 ${formValue.labelSize === option.value ? 'selected' : ''}`}
            >
              {option.text}
            </MuteText>
          </div>
        ))}
      </OptionsContainer>
      <OptionsContainer className="d-flex align-items-center mb-4">
        <BoldText>Page Size</BoldText>
        {pageSizeOptions.map(option => (
          <div
            key={option.key}
            className="ml-4 d-flex align-items-center cursor-pointer"
            onClick={() => changePageSize(option.value)}
          >
            <Radio checked={formValue.pageSize === option.value} />
            <MuteText
              className={`ml-1 ${formValue.pageSize === option.value ? 'selected' : ''}`}
            >
              {option.text}
            </MuteText>
          </div>
        ))}
      </OptionsContainer>
      {data && (
        <div className="d-flex flex-column">
          <BoldText className="mb-1">Previous Export</BoldText>
          <BorderedBox className="d-flex justify-content-between mb-2">
            <div className="d-flex align-items-center">
              <BoldText>
                {DateUtility.formatDate(data?.createdAt, DateFormat.barChartMonth)}
              </BoldText>
              <ColoredPill
                color={locationLabelStatusColors[data?.status] || theme.colors.primary}
                className="ml-2"
              >
                <Circle weight="fill" size={10} />
                {CommonUtility.toTitleCase(data?.status)}
              </ColoredPill>
            </div>
            {data?.downloadUrl && (
              <HoverBorderButton
                size="tiny"
                className="flex-btn mr-0 py-2 px-3"
                onClick={downloadPreviousExport}
              >
                <DownloadSimple size={16} className="mr-1" />
                <span>Download</span>
              </HoverBorderButton>
            )}
          </BorderedBox>
        </div>
      )}
      <div className="d-flex align-items-center justify-content-end pt-2">
        <HoverBorderButton onClick={onClose}>Cancel</HoverBorderButton>
        <StyledButton
          disabled={!(formValue.labelSize && formValue.pageSize)}
          className="ml-2 mr-0"
          onClick={onSubmit}
          loading={loading}
        >
          Generate Labels
        </StyledButton>
      </div>
    </SkupremeModal>
  );
}
